<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref } from 'vue'
import { useApolloClient, useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import useVuelidate from '@vuelidate/core'
import useAsyncDialog from '@/components/dialogs/useAsyncDialog'
import TextField from '@/components/input/TextField.vue'
import StepTypePicker from '@/components/input/StepTypePicker.vue'
import { normalName, required, scrollToFirstInvalidField } from '@/validation'
import {
  AllocateArticleToStepCommand,
  AllocateEbomPartToStepCommand,
  CreateStepInput,
  Mutation,
  SetStepIcons,
  UpdateStep,
} from '@/generated/graphql'

export type DropDecision =
  | { action: 'nest' }
  | { action: 'bundle'; name: string; stepTypeId: string }

type StepDetails = {
  id: string
  name: string
  durationInSeconds: number
  instructionText?: string | null
  warningText?: string | null
  stepType: { id: string }
  toolIds: string[]
  icons: { id: string }[]
  missingIcons: { id: string }[]
  inputs: { id: string; milliQuantity: number; output: { id: string; step: { id: string } } }[]
  outputs: { id: string }[]
  articleAllocations: {
    __typename: string
    milliQuantity: number
    article: { id: string }
    ebomPart?: { id: string }
  }[]
}
type StepDetailsQuery = { product: { moved: StepDetails; target: StepDetails } }

const props = defineProps<{
  moduleId: string
}>()

const { t } = useI18n()
const { client } = useApolloClient()

const stepDetailsQuery = gql`
  query StepDropDecisionData($movedStepId: ID!, $targetStepId: ID!) {
    product {
      moved: step(id: $movedStepId) {
        ...StepDropDecisionStep
      }
      target: step(id: $targetStepId) {
        ...StepDropDecisionStep
      }
    }
  }

  fragment StepDropDecisionStep on Step {
    id
    name
    durationInSeconds
    instructionText
    warningText
    stepType {
      id
    }
    toolIds
    icons {
      id
    }
    missingIcons {
      id
    }
    inputs {
      id
      milliQuantity
      output {
        id
        step {
          id
        }
      }
    }
    outputs {
      id
    }
    articleAllocations {
      __typename
      ... on IArticleAllocation {
        milliQuantity
        article {
          id
        }
      }
      ... on EbomPartAllocation {
        ebomPart {
          id
        }
      }
    }
  }
`

const moved = ref<StepDetails>()
const target = ref<StepDetails>()
const canMoveData = ref(true)
const choice = ref<'bundle' | 'move'>('bundle')

type BundleModel = { name?: string; stepTypeId?: string }
const bundleModel = reactive<BundleModel>({})
const bundleValidation = useVuelidate<BundleModel>(
  { name: { required, normalName }, stepTypeId: { required } },
  bundleModel,
)

const { openAsyncDialog, asyncDialogResolve, asyncDialogLoading, asyncDialogIsOpen } =
  useAsyncDialog<DropDecision>()

function hasData(step: StepDetails) {
  return (
    step.articleAllocations.length > 0 ||
    step.inputs.length > 0 ||
    step.outputs.length > 0 ||
    step.toolIds.length > 0 ||
    step.icons.length + step.missingIcons.length > 0 ||
    !!step.instructionText ||
    !!step.warningText ||
    step.durationInSeconds > 0
  )
}

const summaryItems = computed<string[]>(() => {
  const s = target.value
  if (!s) {
    return []
  }

  const prefix = 'component.stepDropDecisionDialog'
  const items: string[] = []
  if (s.articleAllocations.length > 0) {
    items.push(t(`${prefix}.summaryAllocations`, { count: s.articleAllocations.length }))
  }
  if (s.inputs.length > 0) {
    items.push(t(`${prefix}.summaryInputs`, { count: s.inputs.length }))
  }
  if (s.outputs.length > 0) {
    items.push(t(`${prefix}.summaryOutputs`, { count: s.outputs.length }))
  }
  if (s.toolIds.length > 0) {
    items.push(t(`${prefix}.summaryTools`, { count: s.toolIds.length }))
  }
  const iconCount = s.icons.length + s.missingIcons.length
  if (iconCount > 0) {
    items.push(t(`${prefix}.summaryIcons`, { count: iconCount }))
  }
  if (s.instructionText) {
    items.push(t(`${prefix}.summaryInstructions`))
  }
  if (s.warningText) {
    items.push(t(`${prefix}.summaryWarning`))
  }
  if (s.durationInSeconds > 0) {
    items.push(t(`${prefix}.summaryDuration`))
  }
  return items
})
// Moving an output would break the inputs referencing it; a self-fed input would become a loop.
const unmovableIO = computed(() => {
  if (!target.value || !moved.value) {
    return false
  }

  const movedId = moved.value.id
  return (
    target.value.outputs.length > 0 || target.value.inputs.some((i) => i.output.step.id == movedId)
  )
})

const open = async (
  movedStep: { stepId: string; isLibraryReference: boolean },
  targetStep: { stepId: string; isLibraryReference: boolean },
): Promise<DropDecision | undefined> => {
  const { data } = await client.query<StepDetailsQuery>({
    query: stepDetailsQuery,
    variables: { movedStepId: movedStep.stepId, targetStepId: targetStep.stepId },
    fetchPolicy: 'network-only',
  })
  moved.value = data.product.moved
  target.value = data.product.target
  if (!hasData(data.product.target)) {
    return { action: 'nest' }
  }

  canMoveData.value = !movedStep.isLibraryReference && !targetStep.isLibraryReference
  choice.value = 'bundle'
  bundleModel.name = data.product.target.name
  bundleModel.stepTypeId = data.product.target.stepType.id
  bundleValidation.value.$reset()
  return openAsyncDialog()
}

const transferLoading = ref(false)
async function confirm() {
  if (!asyncDialogResolve.value) {
    return
  }

  if (choice.value == 'bundle') {
    bundleValidation.value.$touch()
    if (bundleValidation.value.$invalid) {
      scrollToFirstInvalidField()
      return
    }

    asyncDialogResolve.value({
      action: 'bundle',
      name: bundleModel.name as string,
      stepTypeId: bundleModel.stepTypeId as string,
    })
    return
  }

  transferLoading.value = true
  try {
    await transferData(target.value as StepDetails, moved.value as StepDetails)
  } finally {
    transferLoading.value = false
  }
  asyncDialogResolve.value({ action: 'nest' })
}

const allocateArticleMutation = useMutation<Mutation, { command: AllocateArticleToStepCommand }>(
  gql`
    mutation StepDropDecisionAllocateArticle($command: AllocateArticleToStepCommand!) {
      article {
        allocateArticleToStep(command: $command)
      }
    }
  `,
)
const allocateEbomPartMutation = useMutation<Mutation, { command: AllocateEbomPartToStepCommand }>(
  gql`
    mutation StepDropDecisionAllocateEbomPart($command: AllocateEbomPartToStepCommand!) {
      article {
        allocateEbomPartToStep(command: $command)
      }
    }
  `,
)
const updateStepMutation = useMutation<Mutation, { step: UpdateStep }>(gql`
  mutation StepDropDecisionUpdateStep($step: UpdateStep!) {
    product {
      updateStep(command: $step) {
        id
      }
    }
  }
`)
const setStepIconsMutation = useMutation<Mutation, { command: SetStepIcons }>(gql`
  mutation StepDropDecisionSetStepIcons($command: SetStepIcons!) {
    product {
      setStepIcons(command: $command) {
        id
      }
    }
  }
`)
const createInputMutation = useMutation<Mutation, { command: CreateStepInput }>(gql`
  mutation StepDropDecisionCreateInput($command: CreateStepInput!) {
    product {
      createStepInput(command: $command) {
        id
      }
    }
  }
`)
const deleteInputMutation = useMutation<Mutation, { id: string }>(gql`
  mutation StepDropDecisionDeleteInput($id: ID!) {
    product {
      deleteStepInput(id: $id)
    }
  }
`)

async function transferData(from: StepDetails, to: StepDetails) {
  for (const input of from.inputs) {
    if (input.output.step.id == to.id) {
      continue
    }

    await createInputMutation.mutate({
      command: {
        stepOutputId: input.output.id,
        milliQuantity: input.milliQuantity,
        moduleId: props.moduleId,
        stepId: to.id,
      },
    })
    await deleteInputMutation.mutate({ id: input.id })
  }

  for (const allocation of from.articleAllocations) {
    if (allocation.__typename == 'EbomPartAllocation' && allocation.ebomPart) {
      const existing = to.articleAllocations
        .filter((a) => a.ebomPart?.id == allocation.ebomPart?.id)
        .reduce((sum, a) => sum + a.milliQuantity, 0)
      await allocateEbomPartMutation.mutate({
        command: {
          ebomPartId: allocation.ebomPart.id,
          stepId: to.id,
          milliQuantity: existing + allocation.milliQuantity,
        },
      })
      await allocateEbomPartMutation.mutate({
        command: { ebomPartId: allocation.ebomPart.id, stepId: from.id, milliQuantity: 0 },
      })
    } else {
      const existing = to.articleAllocations
        .filter(
          (a) => a.__typename == 'ManualArticleAllocation' && a.article.id == allocation.article.id,
        )
        .reduce((sum, a) => sum + a.milliQuantity, 0)
      await allocateArticleMutation.mutate({
        command: {
          articleId: allocation.article.id,
          stepId: to.id,
          milliQuantity: existing + allocation.milliQuantity,
        },
      })
      await allocateArticleMutation.mutate({
        command: { articleId: allocation.article.id, stepId: from.id, milliQuantity: 0 },
      })
    }
  }

  const instructionText =
    [to.instructionText, from.instructionText].filter((i) => !!i).join('\n\n') || undefined
  const warningText =
    [to.warningText, from.warningText].filter((w) => !!w).join('\n\n') || undefined
  await updateStepMutation.mutate({
    step: {
      id: to.id,
      name: to.name,
      stepTypeId: to.stepType.id,
      durationInSeconds: to.durationInSeconds + from.durationInSeconds,
      instructionText,
      warningText,
      toolIds: [...new Set([...to.toolIds, ...from.toolIds])],
    },
  })
  await updateStepMutation.mutate({
    step: {
      id: from.id,
      name: from.name,
      stepTypeId: from.stepType.id,
      durationInSeconds: 0,
      instructionText: undefined,
      warningText: undefined,
      toolIds: [],
    },
  })
  const iconIds = (s: StepDetails) => [...s.icons, ...s.missingIcons].map((i) => i.id)
  await setStepIconsMutation.mutate({
    command: { stepId: to.id, iconIds: [...new Set([...iconIds(to), ...iconIds(from)])] },
  })
  await setStepIconsMutation.mutate({ command: { stepId: from.id, iconIds: [] } })
}

defineExpose({ open })
</script>

<template>
  <v-dialog v-if="asyncDialogResolve" v-model="asyncDialogIsOpen" max-width="650" scrollable>
    <v-card>
      <v-card-title>
        {{ t('component.stepDropDecisionDialog.title') }}

        <v-btn
          density="compact"
          icon="close"
          class="float-right"
          variant="flat"
          @click="asyncDialogResolve()"
        />
      </v-card-title>
      <v-card-text>
        <p>{{ t('component.stepDropDecisionDialog.message', { target: target?.name }) }}</p>

        <div class="my-3">
          <v-chip v-for="item in summaryItems" :key="item" density="compact" class="mr-1 mb-1">
            {{ item }}
          </v-chip>
        </div>

        <v-radio-group v-model="choice" hide-details>
          <v-radio value="bundle" :label="t('component.stepDropDecisionDialog.optionBundle')" />
          <v-radio
            value="move"
            :disabled="!canMoveData"
            :label="t('component.stepDropDecisionDialog.optionMove')"
          />
        </v-radio-group>
        <p v-if="!canMoveData" class="text-medium-emphasis text-body-2">
          {{ t('component.stepDropDecisionDialog.optionMoveUnavailable') }}
        </p>

        <template v-if="choice == 'bundle'">
          <text-field
            v-model="bundleModel.name"
            class="mt-4"
            :label="t('entity.step.field.name')"
            :validation="bundleValidation.name"
            required
          />
          <step-type-picker
            v-model="bundleModel.stepTypeId"
            :label="t('entity.step.field.stepType')"
            :validation="bundleValidation.stepTypeId"
            required
          />
        </template>

        <v-alert v-if="choice == 'move'" type="info" density="compact" class="mt-4">
          <p>{{ t('component.stepDropDecisionDialog.optionMoveHint') }}</p>
          <p v-if="unmovableIO">{{ t('component.stepDropDecisionDialog.ioWarning') }}</p>
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="asyncDialogResolve()">
          {{ t('button.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="asyncDialogLoading || transferLoading"
          @click="confirm"
        >
          {{ t('button.ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss"></style>
